import { memo, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useExecutionMock } from '../mock/ExecutionMockContext.jsx';
import { nodeShell, NODE_COLORS, useZoomLod, NodeLodOverlay } from '../EcaHandleLabel.jsx';
import { getNodeIcon } from '../ecaNodeVisuals.js';

/**
 * Gateway node — flow node (WaitForAll AND join).
 *
 * Axes:
 *   - LEFT handles: one flux input per expected branch (blue)
 *   - RIGHT handle: single flux output / continuation (blue)
 *   - No data handles (flow nodes do not operate on token data)
 *
 * The node owns its own session state via ExecutionMockContext.
 * Branch state (pending / done) is displayed inside the node body.
 * reset_after_fire controls whether the node can fire again after all branches complete.
 */
export const EcaGatewayNode = memo(function EcaGatewayNode({ id, data, selected }) {
  const { initGateway, getSession } = useExecutionMock();
  const lod = useZoomLod();

  const branches = data.branches ?? [];
  const resetAfterFire = !!data.configuration?.reset_after_fire;
  const session = getSession(id);

  // Initialize this gateway's mock session when mounted or branches change
  useEffect(() => {
    if (branches.length > 0) {
      initGateway(id, branches, resetAfterFire);
    }
  }, [id, branches.join(','), resetAfterFire]);

  const branchSpacing = branches.length > 1 ? 80 / (branches.length - 1) : 0;
  const branchTopStart = 20;
  const fired = session?.fired;
  const headerBg = fired ? 'bg-green-600' : 'bg-rose-600';
  const shellColors = fired ? NODE_COLORS.gatewayFired : NODE_COLORS.gateway;

  return (
    // No position:relative here — gateway has no hover labels, handles go directly
    <div style={{ overflow: 'visible', position: 'relative' }}>

      {/* LEFT flux input handles — one per branch */}
      {branches.map((branchId, i) => (
        <Handle
          key={`flux-in-branch-${i}`}
          type="target"
          position={Position.Left}
          id={`flux-in-branch-${i}`}
          style={{
            top: `${branches.length === 1 ? 50 : branchTopStart + i * branchSpacing}%`,
            background: '#3B82F6',
            border: '2px solid #1D4ED8',
            width: 12,
            height: 12,
          }}
        />
      ))}

      {/* RIGHT flux output — continuation */}
      <Handle type="source" position={Position.Right} id="flux-out"
        style={{ background: '#3B82F6', border: '2px solid #1D4ED8', width: 12, height: 12 }} />

      {/* Visual shell */}
      <div style={{ ...nodeShell(shellColors, selected), minWidth: 200, opacity: lod ? 0 : 1, transition: 'opacity 160ms ease' }}>
        {/* Header */}
        <div className={`${headerBg} text-white px-3 py-1.5 flex items-center gap-2`}>
          <span className="text-xs font-bold uppercase tracking-wider opacity-70">Flow</span>
          <span className="text-xs font-medium truncate">{data.label ?? 'Wait for all'}</span>
          {fired && (
            <span className="ml-auto text-xs bg-white text-green-700 px-1.5 py-0.5 rounded font-bold">FIRED</span>
          )}
        </div>

        {/* Branch states */}
        <div className="px-3 py-2 space-y-1">
          {branches.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No branches — connect flux inputs</p>
          ) : (
            branches.map((branchId) => {
              const state = session?.branches?.[branchId] ?? 'pending';
              return (
                <div key={branchId} className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    state === 'done' ? 'bg-green-100 text-green-600 border border-green-400' : 'bg-gray-100 text-gray-400 border border-gray-300'
                  }`}>
                    {state === 'done' ? '✓' : '○'}
                  </span>
                  <span className="text-xs text-gray-600 font-mono truncate">{branchId}</span>
                  <span className={`text-xs ml-auto ${state === 'done' ? 'text-green-500' : 'text-gray-300'}`}>
                    {state}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* reset_after_fire indicator */}
        <div className="border-t border-gray-100 px-3 py-1.5 flex items-center justify-between">
          <span className="text-xs text-gray-400">reset after fire</span>
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
            resetAfterFire ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-gray-50 text-gray-400 border border-gray-200'
          }`}>
            {resetAfterFire ? 'ON' : 'OFF'}
          </span>
        </div>
      </div>

      {/* Low-zoom billboard */}
      <NodeLodOverlay
        active={lod} colors={shellColors} selected={selected}
        icon={getNodeIcon('gateway', data.plugin)}
        title={data.label ?? 'Wait for all'}
      />
    </div>
  );
});
